import { CommonModule, Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  inject,
  ViewChild,
  ElementRef,
  OnDestroy,
  HostBinding
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { I18NService } from '@core';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzDescriptionsModule } from 'ng-zorro-antd/descriptions';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzUploadModule } from 'ng-zorro-antd/upload';
import { finalize, Observable, Subscription } from 'rxjs';
import { HttpService } from 'src/app/http/http.service';
import { ImageService } from 'src/app/services/image.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { showFallbackHttpError } from 'src/app/shared/utils/http-error-toast.util';
import {
  TENANT_CAPABILITY_API_MANAGE_PUBLIC_IP_RANGE,
  TENANT_CAPABILITY_MANAGE_PUBLIC_IP_RANGE
} from 'src/app/shared/utils/tenant-capability.util';
import { ConstantUtil } from 'src/app/util/constant.util';

import { ServicenumberListComponent } from '../../servicenumber/list/servicenumber-list.component';
import { CityData } from '../city-data';
import { IndustryData } from '../industry-data';
import { AiffListComponent } from './aiff/list/aiff-list.component';
import { TenantQuotaUsageComponent } from './quota-usage/tenant-quota-usage.component';
import { TenantCallConfigComponent } from './tenant-call-config/tenant-call-config.component';
import { TenantCapabilityConfigComponent } from './tenant-capability-config/tenant-capability-config.component';
import { TenantConfigComponent } from './tenant-config/tenant-config.component';
import { TenantIntegrationAppsComponent } from './tenant-integration-apps/tenant-integration-apps.component';
import { AvatarUploadModalComponent } from '../../avatar-modal/avatar-upload-modal.component';
import { ImageViewerComponent } from '../../avatar-modal/image-viewer/image-viewer.component';
import { TenantContactListComponent } from '../tenant-contact/list/tenant-contact-list.component';
import { TenantEmployeeComponent } from '../tenant-employee/tenant-employee.component';
import { TenantIpRangeComponent } from '../tenant-employee/tenant-ip-range/tenant-ip-range.component';

@Component({
  selector: 'app-tenant-detail',
  templateUrl: './tenant-detail.component.html',
  styleUrls: ['./tenant-detail.component.less'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzFormModule,
    NzInputModule,
    NzButtonModule,
    NzSelectModule,
    NzCardModule,
    NzTabsModule,
    NzSpinModule,
    NzIconModule,
    NzDividerModule,
    NzTagModule,
    NzDescriptionsModule,
    NzGridModule,
    NzToolTipModule,
    NzModalModule,
    NzSwitchModule,
    NzUploadModule,
    NzAvatarModule,
    NzEmptyModule,
    NzDatePickerModule,
    ServicenumberListComponent,
    NzInputNumberModule,
    TenantConfigComponent,
    TenantCapabilityConfigComponent,
    TenantCallConfigComponent,
    TenantIntegrationAppsComponent,
    NzModalModule,
    AvatarUploadModalComponent,
    TenantEmployeeComponent,
    AiffListComponent,
    TenantQuotaUsageComponent,
    TenantContactListComponent,
    TenantIpRangeComponent,
    ImageViewerComponent,
    I18nPipe,
    TestIdDirective
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TenantDetailComponent implements OnInit, OnDestroy {
  @ViewChild('fileInput') fileInput!: ElementRef;
  private readonly http = inject(HttpService);
  private readonly imageService = inject(ImageService); // 注入 ImageService
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly message = inject(NzMessageService);
  private readonly modal = inject(NzModalService);
  public readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);

  selectedTabIndex = 0;
  currentTabKey = 'basic';
  tenantId: string = '';
  loading: boolean = false;
  tenantForm!: FormGroup;
  tenantData: any = {};
  serviceNumbers: any[] = [];
  certificationInfo: any = {};
  avatarUrl: string = '';
  imageViewerVisible = false;
  private _shouldShowIpRangeTab = false;

  get shouldShowIpRangeTab(): boolean {
    if (this.tenantData?.type === 'Person') {
      return false;
    }
    return this._shouldShowIpRangeTab;
  }

  set shouldShowIpRangeTab(value: boolean) {
    this._shouldShowIpRangeTab = value;
  }

  // 新增：记录路由请求的 tab，避免 setTimeout 等待
  private requestedTab: string | null = null;

  // 头像上传相关属性
  isAvatarModalVisible = false;
  isAvatarUploading = false;
  tenantConfigName: string = this.i18n.fanyi('tenant.detail.tenant-params');
  formChanged: boolean = false;

  uploadData: any = {
    x: 0,
    y: 0,
    size: 80
  };
  // 租户类型选项
  get tenantTypes() {
    return [
      { label: this.i18n.fanyi('tenant.detail.tenant-type.common'), value: 'Common' },
      { label: this.i18n.fanyi('tenant.detail.tenant-type.person'), value: 'Person' },
      { label: this.i18n.fanyi('tenant.detail.tenant-type.public'), value: 'Public' },
      { label: this.i18n.fanyi('tenant.detail.tenant-type.official'), value: 'Official' },
      { label: this.i18n.fanyi('tenant.detail.tenant-type.service'), value: 'Service' }
    ];
  }

  // 行业选项
  industryOptions: any[] = IndustryData;

  // 城市选项
  cityOptions: any[] = CityData;

  // 子行业选项（根据行业动态变化）
  industrySubOptions: any[] = [];

  // 企业规模选项
  get scaleOptions() {
    return [
      { text: this.i18n.fanyi('tenant.detail.scale.mini'), value: 'Mini' },
      { text: this.i18n.fanyi('tenant.detail.scale.smart'), value: 'Smart' },
      { text: this.i18n.fanyi('tenant.detail.scale.middle'), value: 'Middle' },
      { text: this.i18n.fanyi('tenant.detail.scale.large'), value: 'Large' }
    ];
  }

  getScaleText(scale: string): string {
    switch (scale) {
      case 'Mini':
        return this.i18n.fanyi('tenant.detail.scale.mini');
      case 'Smart':
        return this.i18n.fanyi('tenant.detail.scale.smart');
      case 'Middle':
        return this.i18n.fanyi('tenant.detail.scale.middle');
      case 'Large':
        return this.i18n.fanyi('tenant.detail.scale.large');
      default:
        return scale;
    }
  }

  // 认证状态选项
  get certificationStatus() {
    return [
      { label: this.i18n.fanyi('tenant.detail.certification.uncertified'), value: 'Uncertified' },
      { label: this.i18n.fanyi('tenant.detail.certification.in-progress'), value: 'InProgress' },
      { label: this.i18n.fanyi('tenant.detail.certification.certified'), value: 'Certified' },
      { label: this.i18n.fanyi('tenant.detail.certification.failed'), value: 'Failed' }
    ];
  }

  ngOnInit(): void {
    this.initForm();
    this.route.params.subscribe(params => {
      this.tenantId = params['id'];
      this.loadTenantData();
      this.loadTenantCapability();
    });

    // 根据路由参数设置当前标签页（移除 setTimeout，统一通过方法计算）
    this.route.queryParams.subscribe(params => {
      this.requestedTab = params['tab'] || null;
      this.applyTabFromParam();
    });
  }

  @HostBinding('class.from-menu-mode')
  get isFromMenuMode() {
    return this.isSidebarNavigationMode;
  }

  get isSidebarNavigationMode(): boolean {
    const currentUrl = this.router.url;
    return currentUrl.includes('/tenant/detail');
  }

  get shouldShowTopTabs(): boolean {
    return !this.isSidebarNavigationMode;
  }

  get shouldShowQuotaUsageTab(): boolean {
    return this.readCurrentTenantType() === 'Official' && this.tenantData?.type === 'Person';
  }

  get shouldShowCapabilityTab(): boolean {
    return this.tenantData?.type !== 'Person';
  }

  get quotaUsageTabIndex(): number {
    return this.getTabIndex('quota-usage');
  }

  isTabActive(tab: string): boolean {
    return this.currentTabKey === tab || this.selectedTabIndex === this.getTabIndex(tab);
  }

  // 初始化表单
  initForm(): void {
    this.tenantForm = this.fb.group({
      name: [null, [Validators.required]],
      shortName: [null, [Validators.required]],
      code: [{ value: null, disabled: true }, [Validators.required]],
      type: [{ value: 'null', disabled: true }, [Validators.required]],
      industry: [null],
      industrySub: [null],
      scale: [null],
      city: [null],
      startTime: [null],
      endTime: [null, [this.endTimeValidator]],
      description: [null, [Validators.required]], // 添加必填验证
      address: [null],
      website: [null],
      unifiedNumber: [null],
      representativeNumber: [null],
      accountId: [{ value: 'null', disabled: true }],
      avatarId: [null]
    });
  }

  // 结束时间验证器
  endTimeValidator = (control: any): { [key: string]: any } | null => {
    if (!control.value) {
      return null;
    }

    const startTime = this.tenantForm?.get('startTime')?.value;
    if (startTime && control.value < startTime) {
      return { endTimeInvalid: true };
    }
    return null;
  };

  // 开始时间变更时触发结束时间验证
  onStartTimeChange(date: Date): void {
    const endTimeControl = this.tenantForm.get('endTime');
    if (endTimeControl) {
      endTimeControl.updateValueAndValidity();
    }
  }

  // 根据行业更新子行业选项
  updateIndustrySubOptions(industry: string): void {
    if (!industry) {
      this.industrySubOptions = [];
      this.tenantForm.get('industrySub')?.setValue(null);
      return;
    }

    // 如果选择的产业与之前不同，清除子产业选择
    if (this.tenantData.industry !== industry) {
      this.tenantForm.get('industrySub')?.setValue(null);
    }

    this.industrySubOptions = this.industryOptions.find(item => item.value === industry)?.subItems || [];
    this.cdr.detectChanges();
  }

  // 加载租户数据
  loadTenantData(): void {
    this.loading = true;
    this.http
      .post(`${ConstantUtil.ADMIN_TENANT_ITEM}/${this.tenantId}`, {})
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res.success) {
          this.tenantData = res.data.tenantModel;
          this.patchFormValues();
          if (this.tenantData?.type === 'Person') {
            const activeTab = this.requestedTab || this.currentTabKey;
            if (activeTab === 'ip-range' || activeTab === 'capability') {
              this.requestedTab = 'basic';
              this.currentTabKey = 'basic';
              this.selectedTabIndex = 0;
              const url = this.router
                .createUrlTree([], {
                  relativeTo: this.route,
                  queryParams: { tab: 'basic' },
                  queryParamsHandling: 'merge'
                })
                .toString();
              this.location.go(url);
            }
          }
          // 数据到位后再次根据路由参数校正当前 tab（避免原来用 setTimeout 等待）
          this.applyTabFromParam();
          this.cdr.detectChanges();
        } else {
          this.message.error(this.i18n.fanyi('tenant.detail.load-tenant-info-failed'));
        }
      });
  }

  loadTenantCapability(): void {
    if (!this.tenantId) {
      return;
    }
    this.http.post(`${ConstantUtil.TENANT_CAPABILITY_V1_ITEM}/${this.tenantId}`, {}).subscribe({
      next: res => {
        const capabilities: string[] = res?.data?.capabilities ?? [];
        // 保留當前正在操作的 tab key，避免重新載入能力時因 tab 增加/減少導致索引錯位或回跳
        const activeTabKey = this.currentTabKey || this.getTabKeyByIndex(this.selectedTabIndex);

        this.shouldShowIpRangeTab =
          capabilities.includes(TENANT_CAPABILITY_API_MANAGE_PUBLIC_IP_RANGE) ||
          capabilities.includes(TENANT_CAPABILITY_MANAGE_PUBLIC_IP_RANGE) ||
          capabilities.includes('DeviceNetworkAccessControl') ||
          capabilities.includes('tenant.deviceNetworkAccessControl') ||
          capabilities.includes('ClientAccessControl') ||
          capabilities.includes('tenant.clientAccessControl') ||
          capabilities.includes('PublicIpRangeManage');

        // 若當前正位於 ip-range 且該能力已被關閉或不適用，回退至能力配置頁或基本頁
        if (!this.shouldShowIpRangeTab && (activeTabKey === 'ip-range' || this.requestedTab === 'ip-range')) {
          const fallbackTab = this.shouldShowCapabilityTab ? 'capability' : 'basic';
          this.currentTabKey = fallbackTab;
          this.requestedTab = fallbackTab;
        } else {
          this.currentTabKey = activeTabKey;
          this.requestedTab = activeTabKey;
        }

        const newIndex = this.getTabIndex(this.currentTabKey);
        this.selectedTabIndex = newIndex >= 0 ? newIndex : 0;

        const url = this.router
          .createUrlTree([], {
            relativeTo: this.route,
            queryParams: { tab: this.currentTabKey },
            queryParamsHandling: 'merge'
          })
          .toString();
        this.location.go(url);

        this.cdr.detectChanges();
      },
      error: () => {}
    });
  }
  // 填充表单值
  private formSubscription: Subscription | null = null;
  // 填充表单值
  patchFormValues(): void {
    // 处理日期字段
    const startTime = this.tenantData.startTime ? new Date(this.tenantData.startTime) : null;
    const endTime = this.tenantData.endTime ? new Date(this.tenantData.endTime) : null;

    this.tenantForm.patchValue({
      name: this.tenantData.name,
      shortName: this.tenantData.shortName,
      code: this.tenantData.code,
      type: this.tenantData.type,
      industry: this.tenantData.industry,
      industrySub: this.tenantData.industrySub,
      scale: this.tenantData.scale,
      city: this.tenantData.city,
      startTime: startTime,
      endTime: endTime,
      description: this.tenantData.description,
      address: this.tenantData.address,
      website: this.tenantData.website,
      unifiedNumber: this.tenantData.unifiedNumber,
      representativeNumber: this.tenantData.representativeNumber,
      accountId: this.tenantData.accountId,
      avatarId: this.tenantData.avatarId
    });

    // 如果有行业值，更新子行业选项
    if (this.tenantData.industry) {
      this.updateIndustrySubOptions(this.tenantData.industry);
    }
    if (this.tenantData.avatarId) {
      this.loadAvatarImage(this.tenantData.avatarId);
    }
    // 取消之前的订阅，避免重复订阅
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }

    // 重新订阅表单变更事件
    this.formSubscription = this.tenantForm.valueChanges.subscribe(() => {
      this.formChanged = true;
    });

    // 重置表单变更状态
    this.formChanged = false;
  }
  ngOnDestroy(): void {
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
  }
  loadAvatarImage(avatarId: string): void {
    this.imageService
      .loadImage(avatarId, 'M')
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(imageUrl => {
        if (imageUrl) {
          this.avatarUrl = imageUrl;
          this.cdr.detectChanges();
        } else {
          console.error(this.i18n.fanyi('tenant.detail.load-avatar-failed'));
          this.avatarUrl = '';
          this.cdr.detectChanges();
        }
      });
  }
  canDeactivate = (fromIndex: number, toIndex: number): boolean | Observable<boolean> => {
    // 只有从基础信息页(索引为0)切换且有未保存的修改时才提示
    if (fromIndex === 0 && this.formChanged) {
      return new Observable<boolean>(observer => {
        this.modal.confirm({
          nzTitle: this.i18n.fanyi('tenant.detail.confirm-leave'),
          nzContent: this.i18n.fanyi('tenant.detail.confirm-leave-content'),
          nzOkText: this.i18n.fanyi('tenant.detail.confirm'),
          nzCancelText: this.i18n.fanyi('tenant.detail.cancel'),
          nzOnOk: () => {
            // 重新加载表单数据，恢复到原始状态
            this.patchFormValues();
            observer.next(true);
            observer.complete();
          },
          nzOnCancel: () => {
            observer.next(false);
            observer.complete();
          }
        });
      });
    }
    // 其他情况允许切换
    return true;
  };

  // 保存租户信息
  saveTenant(): void {
    if (this.tenantForm.invalid) {
      Object.values(this.tenantForm.controls).forEach(control => {
        if (control.invalid) {
          control.markAsDirty();
          control.updateValueAndValidity({ onlySelf: true });
        }
      });
      return;
    }

    const formData = this.tenantForm.getRawValue(); // 使用getRawValue获取包括禁用字段在内的所有值

    // 处理日期字段，转换为时间戳(long类型)
    const startTime = formData.startTime ? new Date(formData.startTime).getTime() : null;
    const endTime = formData.endTime ? new Date(formData.endTime).getTime() : null;

    // 处理下拉选择框的null值，转换为空字符串
    const processedData = {
      ...formData,
      industry: formData.industry ?? '',
      industrySub: formData.industrySub ?? '',
      scale: formData.scale ?? '',
      city: formData.city ?? '',
      website: this.normalizeOptionalUrl(formData.website)
    };

    const saveData = {
      id: this.tenantId,
      ...processedData,
      startTime: startTime,
      endTime: endTime
    };

    this.loading = true;
    this.http
      .post(ConstantUtil.ADMIN_TENANT_UPDATE, saveData)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res.success) {
          this.message.success(this.i18n.fanyi('tenant.detail.update-success'));
          this.loadTenantData(); // 重新加载数据
        } else {
          this.message.error(this.i18n.fanyi('tenant.detail.update-failed'));
        }
      });
  }

  private normalizeOptionalUrl(value: string | null): string {
    const trimmedValue = value?.trim() ?? '';
    return trimmedValue ? trimmedValue : '';
  }

  // 返回列表页
  get shouldShowBackButton(): boolean {
    const currentUrl = this.router.url;
    // 只有当路由为 tenants/detail 时才显示返回按钮
    // tenant/detail 时不显示
    return currentUrl.includes('/tenants/detail');
  }
  goBack(): void {
    this.router.navigateByUrl('/tenants'); // 直接导航到租戶清單页面
  }

  // 获取认证状态标签颜色
  getCertificationStatusColor(status: string): string {
    const colorMap: { [key: string]: string } = {
      Uncertified: 'default',
      InProgress: 'processing',
      Certified: 'success',
      Failed: 'error'
    };
    return colorMap[status] ?? 'default';
  }

  // 获取认证状态标签文本
  getCertificationStatusText(status: string): string {
    const textMap: { [key: string]: string } = {
      Uncertified: this.i18n.fanyi('tenant.detail.certification.uncertified'),
      InProgress: this.i18n.fanyi('tenant.detail.certification.in-progress'),
      Certified: this.i18n.fanyi('tenant.detail.certification.certified'),
      Failed: this.i18n.fanyi('tenant.detail.certification.failed')
    };
    return textMap[status] || status;
  }

  // 获取租户类型标签颜色
  getTenantTypeColor(type: string): string {
    const colorMap: { [key: string]: string } = {
      Common: 'blue',
      Person: 'green',
      Public: 'purple',
      Official: 'red',
      Service: 'orange'
    };
    return colorMap[type] ?? 'default';
  }

  // 获取租户类型标签文本
  getTenantTypeText(type: string): string {
    const textMap: { [key: string]: string } = {
      Common: this.i18n.fanyi('tenant.detail.tenant-type.common'),
      Person: this.i18n.fanyi('tenant.detail.tenant-type.person'),
      Public: this.i18n.fanyi('tenant.detail.tenant-type.public'),
      Official: this.i18n.fanyi('tenant.detail.tenant-type.official'),
      Service: this.i18n.fanyi('tenant.detail.tenant-type.service')
    };
    return textMap[type] || type;
  }
  // 显示头像上传对话框
  showAvatarUploadModal(): void {
    this.isAvatarModalVisible = true;
  }

  // 取消头像上传
  handleAvatarModalCancel(): void {
    this.isAvatarModalVisible = false;
  }

  // 确认头像上传
  handleAvatarCropped(event: { blob: Blob; width: number }): void {
    this.isAvatarUploading = true;

    const file = new File([event.blob], 'avatar.png', { type: 'image/png' });

    this.uploadData.id = this.tenantId;
    this.uploadData.size = event.width;

    // 发送请求
    this.http.postFormData(ConstantUtil.ADMIN_TENANT_AVATAR_SET, this.uploadData, file).subscribe({
      next: res => {
        if (res.success) {
          this.message.success(this.i18n.fanyi('tenant.detail.avatar-upload-success'));
          if (res.data) {
            this.tenantForm.patchValue({ avatarId: res.data });
          }
          this.loadAvatarImage(res.data); // 重新加载数据，包括头像
        } else {
          showFallbackHttpError(this.message, res.msg, this.i18n.fanyi('tenant.detail.avatar-upload-failed'));
        }
        this.isAvatarUploading = false;
        this.isAvatarModalVisible = false;
        this.cdr.detectChanges();
      },
      error: error => {
        console.error(this.i18n.fanyi('tenant.detail.avatar-upload-error'), error);
        this.message.error(this.i18n.fanyi('tenant.detail.avatar-upload-failed'));
        this.isAvatarUploading = false;
        this.cdr.detectChanges();
      }
    });
  }
  updateConfigName(name: string): void {
    if (name) {
      this.tenantConfigName = name;
    }
  }

  // 处理标签页切换事件
  onTabChange(event: number | { index: number }): void {
    const index = typeof event === 'number' ? event : (event?.index ?? this.selectedTabIndex);
    this.selectedTabIndex = index;
    const tabKey = this.getTabKeyByIndex(index);
    this.currentTabKey = tabKey;
    this.requestedTab = tabKey;
    const queryParams: any = {
      tab: tabKey
    };

    // 使用Location服务更新URL，不重新加载页面
    const url = this.router
      .createUrlTree([], {
        relativeTo: this.route,
        queryParams: queryParams,
        queryParamsHandling: 'merge'
      })
      .toString();

    this.location.go(url);
  }
  openImageViewer(imageUrl: string): void {
    this.imageViewerVisible = true;
  }
  closeImageViewer(): void {
    this.imageViewerVisible = false;
  }
  // 新增：根据路由参数与 officialServiceNumberId 统一计算当前 tab
  private applyTabFromParam(): void {
    const tab = this.requestedTab;
    if (!tab) {
      this.selectedTabIndex = 0;
      this.currentTabKey = 'basic';
      return;
    }

    if (tab === 'basic') {
      this.selectedTabIndex = 0;
      this.currentTabKey = 'basic';
    } else if (tab === 'channel') {
      // 渠道绑定参数改为独立路由页，不再在详情页 tab 内展示
      this.router.navigate(['/tenant/channel-binding', this.tenantId]);
      this.selectedTabIndex = 0;
      this.currentTabKey = 'basic';
    } else if (tab === 'certification') {
      this.router.navigate(['/tenant/authentication', this.tenantId]);
      this.selectedTabIndex = 0;
      this.currentTabKey = 'basic';
    } else {
      const index = this.getTabIndex(tab);
      if (index >= 0) {
        this.selectedTabIndex = index;
        this.currentTabKey = tab;
      } else {
        this.selectedTabIndex = 0;
        this.currentTabKey = 'basic';
        if (this.requestedTab && this.requestedTab !== 'basic') {
          const url = this.router
            .createUrlTree([], {
              relativeTo: this.route,
              queryParams: { tab: 'basic' },
              queryParamsHandling: 'merge'
            })
            .toString();
          this.location.go(url);
        }
      }
    }
  }

  // 新增：*ngFor trackBy，减少大数据选项列表渲染成本
  trackByValue(index: number, item: any) {
    return item?.value ?? item;
  }

  private readCurrentTenantType(): string | null {
    const tenantStr = localStorage.getItem('current_tenant');
    if (!tenantStr) {
      return null;
    }

    try {
      const tenant = JSON.parse(tenantStr);
      return tenant?.type ?? null;
    } catch {
      return null;
    }
  }

  private getDetailTabKeys(): string[] {
    const keys = ['basic'];
    if (this.shouldShowQuotaUsageTab) {
      keys.push('quota-usage');
    }
    keys.push('servicenumbers', 'employees');
    if (this.shouldShowIpRangeTab) {
      keys.push('ip-range');
    }
    keys.push('config');
    if (this.shouldShowCapabilityTab) {
      keys.push('capability');
    }
    keys.push('call-config', 'integration-apps', 'aiff', 'contacts');
    return keys;
  }

  private getTabKeyByIndex(index: number): string {
    return this.getDetailTabKeys()[index] ?? 'basic';
  }

  private getTabIndex(tab: string): number {
    return this.getDetailTabKeys().indexOf(tab);
  }
}
